
/**
 * @file useReceiptScanner.ts
 * @description This hook provides core functionality for the receipt scanner feature.
 * !!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!
 * !! WARNING: This file is considered feature-complete and should not be modified unless absolutely !!
 * !! necessary. Any changes may disrupt the stable functionality of the receipt scanning feature.  !!
 * !! If changes are required, please consult with the team and thoroughly test all functionality.  !!
 * !!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!
 */

import { useState, useCallback } from "react";
import heic2any from "heic2any";

export const useReceiptScanner = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [capturedImage, setCapturedImage] = useState<string | null>(null);
  
  const openScanner = useCallback(() => {
    console.log("Opening receipt scanner");
    setIsOpen(true);
    setCapturedImage(null);
    setIsProcessing(false);
  }, []);
  
  const closeScanner = useCallback(() => {
    console.log("Closing receipt scanner");
    setIsOpen(false);
    setTimeout(() => {
      setIsProcessing(false);
      setCapturedImage(null);
    }, 300); // Small delay for closing animation
  }, []);

  // Process and optimize image before capture
  const processImageForAPI = useCallback(async (imageSrc: string): Promise<string> => {
    console.log("Processing image for API");
    return new Promise(async (resolve, reject) => {
      try {
        // For data URLs, convert to blob first
        const fetchResponse = await fetch(imageSrc);
        let imageBlob = await fetchResponse.blob();
        
        // Check if the image is HEIC/HEIF format
        if (imageBlob.type.includes('heic') || imageBlob.type.includes('heif')) {
          console.log("Converting HEIC/HEIF image to JPEG");
          try {
            const convertedBlob = await heic2any({
              blob: imageBlob,
              toType: "image/jpeg",
              quality: 0.8
            });
            
            // Handle the result which can be a single Blob or an array of Blobs
            if (Array.isArray(convertedBlob)) {
              // If it's an array, use the first blob
              imageBlob = convertedBlob[0];
            } else {
              // If it's a single blob
              imageBlob = convertedBlob;
            }
          } catch (conversionError) {
            console.error("Error converting HEIC/HEIF:", conversionError);
            throw new Error("Failed to convert image format");
          }
        }

        // Convert blob back to data URL for further processing
        const reader = new FileReader();
        reader.onload = async () => {
          try {
            const dataURL = reader.result as string;
            const img = new Image();
            
            img.onload = () => {
              try {
                // Create canvas for image processing
                const canvas = document.createElement('canvas');
                
                // Calculate new dimensions (max width 1000px)
                let width = img.width;
                let height = img.height;
                
                if (width > 1000) {
                  const ratio = 1000 / width;
                  width = 1000;
                  height = height * ratio;
                }
                
                // Set canvas dimensions
                canvas.width = width;
                canvas.height = height;
                
                // Draw and resize image on canvas
                const ctx = canvas.getContext('2d');
                if (!ctx) {
                  reject(new Error("Could not get canvas context"));
                  return;
                }
                
                // Clear canvas and draw image
                ctx.clearRect(0, 0, width, height);
                ctx.drawImage(img, 0, 0, width, height);
                
                // Convert to JPEG format with set quality
                const optimizedImage = canvas.toDataURL('image/jpeg', 0.85);
                console.log("Image processed successfully");
                resolve(optimizedImage);
              } catch (canvasError) {
                console.error("Error processing image on canvas:", canvasError);
                reject(canvasError);
              }
            };
            
            img.onerror = (error) => {
              console.error("Error loading image for processing:", error);
              reject(error);
            };
            
            img.crossOrigin = "anonymous";
            img.src = dataURL;
          } catch (error) {
            console.error("Error in image processing:", error);
            reject(error);
          }
        };
        
        reader.onerror = (error) => {
          console.error("Error reading blob:", error);
          reject(error);
        };
        
        reader.readAsDataURL(imageBlob);
      } catch (error) {
        console.error("Error in processImageForAPI:", error);
        reject(error);
      }
    });
  }, []);

  const handleCapture = useCallback(async (imageSrc: string) => {
    console.log("Receipt image captured");
    setCapturedImage(imageSrc);
    setIsProcessing(true); // Immediately set to processing mode
  }, []);

  const resetScanner = useCallback(() => {
    setCapturedImage(null);
    setIsProcessing(false);
  }, []);

  return {
    isOpen,
    isProcessing,
    setIsProcessing,
    capturedImage,
    openScanner,
    closeScanner,
    handleCapture,
    resetScanner,
    processImageForAPI
  };
};
